/**
 * WebSocket service for interpreter queue management.
 *
 * This client speaks the same protocol as the local `vrs-server` queue
 * backend at `/ws`.
 */
import { getUserRole } from '../base/user-role/functions';
import { getAppName, isNativeRuntime } from '../base/whitelabel/functions';
import { mobileLog } from '../mobile/navigation/logging';
import { getNotificationDeviceId, registerAlertNotificationToken, registerCurrentNotificationDevice, registerRingStopToken } from '../notifications/deviceRegistration';
import { getApiOrigin, getApiSessionIdentity, refreshAuthSession } from '../shared/api-client';
import { vrsAuthService } from '../vrs-auth/VRSSAuthService';
import { isObserverRoomPage } from '../vrs-auth/observerRoomPage';
import { getPersistentJson, getStorageGeneration, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
import { profilePhotoPath } from '../vrs-layout/profilePhoto';
import { normalizeRegionGrants } from '../../../shared/service-regions';
import { watchAlertPayloadSchema } from '../../../contracts/watch-alerts';
import { getInterpretedStartWithVideoMuted } from './mediaDefaults';
import { withPublicNumber } from './providerDisplayName';
import { callHoldNoticeSchema, callInterpreterChangedPayloadSchema, callMergeDoneSchema, callMergedSchema, callPhoneLegHandedOverPayloadSchema, callTransferOfferEndedPayloadSchema, callTransferOfferPayloadSchema, callTransferPhoneLegFailedPayloadSchema, heldCallStatusSchema, teamJoinedPayloadSchema, teamOfferEndedPayloadSchema, teamOfferPayloadSchema, teamStatePayloadSchema, callWaitingIncomingSchema, callWaitingRespondedSchema, externalMeetingCallPayloadSchema, externalMeetingStatusAckPayloadSchema, meetingDeskStatusPayloadSchema, groupJoinRequestSchema, groupJoinResolvedSchema, groupJoinWaitingSchema, missedCallSchema, queueMessageSchema } from '../../../contracts';
import { CONNECTION_ERROR_LOG_INTERVAL, PENDING_AUTHED_MESSAGE_LIMIT, RECONNECT_STABILITY_WINDOW_MS, SEND_WARNING_LOG_INTERVAL, getCurrentRoomName, getErrorMessage, getHandoffDeviceId, getPresenceHeartbeatPayload, getStoredAuthToken, getStoredJson, getVRSConfig, hasQueueAuthForRole, isAccessTokenExpired, isRecord, normalizeP2PPhoneTarget, numberValue, recordPayload, stringValue } from './queueServiceUtils';
import { normalizeCallPeers, normalizeInterpreterRequest, normalizeMatchPayload, normalizeNotificationPayload, normalizeP2PInvitePayload, normalizeQueueStatus, normalizeRequestQueued, normalizeRingEndedPayload, normalizeVoicemailPayload, normalizeVriInvitePrepared } from './queueMessageNormalizers';
import { getDefaultInterpreterLanguages, getDefaultRequestLanguage } from './interpreterLanguages';
/**
 * Write pushed service-region grants into the cached account (vrs_user_info),
 * normalized as the server stores them, for a signed-in client only. null is
 * "not designated" (no picker; the server decides). undefined when there is no
 * client account cached to update.
 */
function cacheServiceRegions(value) {
    const user = getPersistentJson('vrs_user_info');
    if (!user || (user.role && user.role !== 'client')) {
        return undefined;
    }
    const serviceRegions = normalizeRegionGrants(value);
    setPersistentItem('vrs_user_info', JSON.stringify({ ...user, serviceRegions }));
    return serviceRegions;
}
/**
 * Keep the signed-in interpreter's or captioner's public number (auth_success)
 * in the cached account, so their room shows "Interpreter #1042"
 * (providerDisplayName.ts) even when they signed in before the server sent it.
 */
function cachePublicNumber(message) {
    const updated = withPublicNumber(getPersistentJson('vrs_user_info'), {
        ...'publicNumber' in message ? { publicNumber: message.publicNumber } : {},
        role: message.role,
        userId: message.userId
    });
    if (updated) {
        setPersistentItem('vrs_user_info', JSON.stringify(updated));
    }
}
function accountCallHoldPayload(data) {
    const payload = isRecord(data) ? data : {};
    const role = payload.role === 'client' || payload.role === 'interpreter' ? payload.role : undefined;
    const roomName = stringValue(payload.roomName);
    return {
        role,
        callId: stringValue(payload.callId),
        roomName: roomName || null,
        device: stringValue(payload.device),
        since: stringValue(payload.since) || null,
        canMoveHere: payload.canMoveHere === true,
        recheckAfterMs: numberValue(payload.recheckAfterMs),
        message: stringValue(payload.message)
    };
}
function callMovePayload(data) {
    const payload = isRecord(data) ? data : {};
    return {
        callId: stringValue(payload.callId),
        roomName: stringValue(payload.roomName) || null,
        device: stringValue(payload.device),
        message: stringValue(payload.message)
    };
}
/**
 * Whether this is a web call-room page (not a portal or dashboard). Sent at
 * sign-in so an interpreter's room mid-call keeps its socket when a portal
 * tab of the same browser opens.
 */
function isWebRoomPage() {
    if (isNativeRuntime() || typeof window === 'undefined') {
        return false;
    }
    const last = window.location?.pathname?.split('/').filter(Boolean).pop();
    return Boolean(last) && !last.includes('.');
}
/**
 * Whether this page is a room a portal is prerendering, or one just shown
 * whose gate has not released it yet: no socket may open before that.
 */
export function isRoomActivationHeld() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }
    const gate = window.__vrsActivation;
    return document.prerendering === true
        || (typeof gate?.isActivated === 'function' && !gate.isActivated());
}
/**
 * Under the server's CLIENT_DISCONNECT_GRACE_MS (12 s): when another socket
 * of the account closes, its presence row reads disconnected until a live
 * socket heartbeats, and a process without this socket then treats the client
 * as unreachable (an interpreter's accept would cancel the match). The room
 * pill's second socket used to cover this at 10 s; it is the only one now.
 */
const QUEUE_HEARTBEAT_INTERVAL_MS = 10000;
/**
 * The server answers each heartbeat (heartbeat_ack). When two heartbeats in a
 * row get no message back, the socket is dead even if it reads open (a phone
 * that slept, a changed network), and the watchdog opens a new one.
 */
const QUEUE_WATCHDOG_UNANSWERED_HEARTBEATS = 2;
class InterpreterQueueService {
    constructor() {
        this.ws = null;
        this.lastSentInterpreterStatus = null;
        // Malka Terp: this build rings a queue offer like a call while the app is
        // off the screen (set by the native call integration), whether the server
        // does too (auth_success), and whether the app is on screen now
        // (vrs-server/ws/interpreter-standby.js).
        this.standbyRingSupported = false;
        this.serverStandbyRing = false;
        this.appState = 'active';
        /**
         * Set while the interpreter's wrap-up owns the return to the queue: a
         * reconnect must not re-announce Available before the breather ends.
         */
        this.availabilityReplayHeld = false;
        this.lastInterpreterName = null;
        this.lastInterpreterLanguages = getDefaultInterpreterLanguages();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.baseReconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.stabilityTimer = null;
        this.reconnectTimeout = null;
        this.heartbeatInterval = null;
        // Heartbeats sent since the last message from the server (the watchdog).
        this.unansweredHeartbeats = 0;
        this.listeners = new Map();
        // Counts disconnect(), which removes every listener. A module that
        // registers its listeners once registers them again when this changes.
        this.listenerGeneration = 0;
        this.userId = null;
        this.shouldReconnect = true;
        // Latched when the server supersedes this socket because the same account
        // connected from another tab on this device. While set, NOTHING may
        // reopen the socket implicitly — two tabs each auto-reconnecting take
        // turns superseding each other in an endless online/offline war. Only an
        // explicit user action (toggling availability, a fresh page load) clears
        // it.
        this.superseded = false;
        this.lastConnectionErrorLogAt = 0;
        this.connectionErrorCount = 0;
        this.lastSendWarningLogAt = 0;
        this.presenceEventCleanup = null;
        this.authRefreshAttempted = false;
        this.authenticated = false;
        this.pendingAuthedMessages = [];
        // Set while the server holds this session out because another device of
        // the same interpreter account is in a live call (session_held_elsewhere).
        // The socket stays open but unauthenticated; nothing may re-send auth on
        // its own — only an explicit retakeSession() after the call has ended.
        this.heldElsewhere = false;
        // Calls that moved to another device of this client account. This device
        // leaving them must never send call_end: the call continues elsewhere.
        this.movedAwayCallIds = new Set();
        // A connect asked for while the room page was prerendering, waiting for
        // the page to be shown.
        this.connectAfterActivationPending = false;
        this.conferenceResponses = new Map();
        this.conferenceResponseTimers = new Map();
        this.config = getVRSConfig();
        this.userRole = getUserRole();
        this.installPresenceEventListeners();
        if (hasQueueAuthForRole(this.userRole)) {
            this.connect();
        }
    }
    installPresenceEventListeners() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }
        const sendPresenceSoon = () => setTimeout(() => this.ping(), 0);
        document.addEventListener('visibilitychange', sendPresenceSoon);
        window.addEventListener('focus', sendPresenceSoon);
        window.addEventListener('online', sendPresenceSoon);
        this.presenceEventCleanup = () => {
            document.removeEventListener('visibilitychange', sendPresenceSoon);
            window.removeEventListener('focus', sendPresenceSoon);
            window.removeEventListener('online', sendPresenceSoon);
        };
    }
    connect() {
        // A manager's hidden observation is never the saved account.
        if (!this.shouldReconnect || isObserverRoomPage())
            return;
        // Backstop: the room page's gate holds the whole app while a portal
        // prerenders it, so this should never run then; if it does, the
        // socket opens once, when the page is shown.
        if (isRoomActivationHeld()) {
            this.connectAfterActivation();
            return;
        }
        this.userRole = getUserRole();
        this.config = getVRSConfig();
        if (!hasQueueAuthForRole(this.userRole)) {
            return;
        }
        if (this.superseded) {
            mobileLog('warn', 'queue_ws_connect_blocked_superseded', {}, { console: false });
            return;
        }
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        try {
            const wsUrl = this.config.queueServiceUrl;
            const socket = new WebSocket(wsUrl);
            this.ws = socket;
            const origin = getApiOrigin();
            const generation = getStorageGeneration();
            const ownsSocket = () => this.ws === socket && this.shouldReconnect
                && origin === getApiOrigin() && generation === getStorageGeneration();
            socket.onopen = () => {
                if (!ownsSocket())
                    return;
                // Only treat the connection as healthy — and reset the
                // reconnect backoff — after it survives a stability window.
                // Captive portals and IPS middleboxes complete the handshake
                // and then kill the socket within a second; resetting on open
                // turns exponential backoff into a tight forever-loop.
                this.clearStabilityTimer();
                this.stabilityTimer = setTimeout(() => {
                    if (!ownsSocket())
                        return;
                    this.stabilityTimer = null;
                    this.reconnectAttempts = 0;
                }, RECONNECT_STABILITY_WINDOW_MS);
                this.authRefreshAttempted = false;
                this.authenticated = false;
                this.startHeartbeat();
                this.sendAuth();
                this.emit('connection', { connected: true });
            };
            socket.onmessage = event => {
                if (!ownsSocket())
                    return;
                this.unansweredHeartbeats = 0;
                try {
                    const parsed = queueMessageSchema.safeParse(JSON.parse(event.data));
                    if (parsed.success) {
                        this.handleMessage(parsed.data);
                    }
                    else {
                        mobileLog('warn', 'queue_ws_contract_parse_failed', {
                            issueCount: parsed.error.issues.length
                        }, { console: false });
                    }
                }
                catch (error) {
                    console.error('Error parsing queue message:', error);
                }
            };
            socket.onclose = () => {
                if (!ownsSocket())
                    return;
                this.ws = null;
                this.clearStabilityTimer();
                this.authenticated = false;
                this.stopHeartbeat();
                this.emit('connection', { connected: false });
                if (this.shouldReconnect) {
                    this.attemptReconnect();
                }
            };
            socket.onerror = error => {
                if (!ownsSocket())
                    return;
                this.reportConnectionIssue(error);
            };
        }
        catch (error) {
            this.reportConnectionIssue(error);
            this.attemptReconnect();
        }
    }
    connectAfterActivation() {
        if (this.connectAfterActivationPending) {
            return;
        }
        this.connectAfterActivationPending = true;
        const resume = () => {
            this.connectAfterActivationPending = false;
            this.connect();
        };
        const whenActivated = window.__vrsWhenActivated;
        if (typeof whenActivated === 'function') {
            whenActivated(resume);
        }
        else {
            document.addEventListener('prerenderingchange', resume, { once: true });
        }
    }
    reportConnectionIssue(error) {
        this.connectionErrorCount++;
        const now = Date.now();
        const shouldLog = this.lastConnectionErrorLogAt === 0
            || now - this.lastConnectionErrorLogAt >= CONNECTION_ERROR_LOG_INTERVAL;
        const payload = {
            code: 'QUEUE_WS_CONNECTION_FAILED',
            errorCount: this.connectionErrorCount,
            message: getErrorMessage(error, 'Queue WebSocket connection failed'),
            retrying: this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts,
            url: this.config.queueServiceUrl
        };
        if (!shouldLog) {
            return;
        }
        this.lastConnectionErrorLogAt = now;
        mobileLog('warn', 'queue_ws_connection_failed', payload, { console: false });
        this.emit('error', payload);
    }
    clearStabilityTimer() {
        if (this.stabilityTimer) {
            clearTimeout(this.stabilityTimer);
            this.stabilityTimer = null;
        }
    }
    startHeartbeat() {
        this.stopHeartbeat();
        this.unansweredHeartbeats = 0;
        this.heartbeatInterval = setInterval(() => {
            if (this.unansweredHeartbeats >= QUEUE_WATCHDOG_UNANSWERED_HEARTBEATS) {
                this.replaceSilentSocket();
                return;
            }
            this.unansweredHeartbeats++;
            this.send({ type: 'heartbeat', data: getPresenceHeartbeatPayload() });
        }, QUEUE_HEARTBEAT_INTERVAL_MS);
    }
    /**
     * The watchdog: the open socket got no message for two heartbeats. Close
     * it as if the server had closed it, and connect again on the normal
     * retry steps.
     */
    replaceSilentSocket() {
        mobileLog('warn', 'queue_ws_silent_socket_replaced', {
            unansweredHeartbeats: this.unansweredHeartbeats
        }, { console: false });
        this.closeSocket();
        this.clearStabilityTimer();
        this.stopHeartbeat();
        this.authenticated = false;
        this.emit('connection', { connected: false });
        if (this.shouldReconnect) {
            this.attemptReconnect();
        }
    }
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }
    attemptReconnect() {
        if (!this.shouldReconnect) {
            return;
        }
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.emit('connection', {
                connected: false,
                maxAttemptsReached: true,
                message: 'Unable to reach the queue server.'
            });
            return;
        }
        this.reconnectAttempts++;
        const exponentialDelay = Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
        const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
        const delay = Math.round(exponentialDelay + jitter);
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect();
        }, delay);
    }
    sendAuth() {
        const storedToken = getStoredAuthToken();
        // Use the same checked refresh outcome for startup and server rejection.
        if (storedToken?.token && isAccessTokenExpired(storedToken.token) && !this.authRefreshAttempted) {
            this.authRefreshAttempted = true;
            void this.refreshAndReauthenticate({ code: 'TOKEN_EXPIRED' });
            return;
        }
        this.sendAuthMessage(storedToken?.token);
    }
    authReplyIsCurrent(socket, identity, origin, token) {
        return this.ws === socket && this.shouldReconnect && getApiOrigin() === origin
            && (token ? getStoredAuthToken()?.token === token : getApiSessionIdentity() === identity);
    }
    sendAuthMessage(token) {
        const storedToken = getStoredAuthToken();
        const storedUser = getStoredJson('vrs_user_info');
        const fallbackUserId = storedUser?.id || storedToken?.userId || `${this.userRole}-${Date.now()}`;
        const fallbackName = storedUser?.name || storedToken?.name || (this.userRole === 'interpreter' ? 'Interpreter' : 'Guest');
        this.send({
            type: 'auth',
            role: this.userRole,
            userId: fallbackUserId,
            name: fallbackName,
            token,
            deviceId: getHandoffDeviceId(),
            ...(isWebRoomPage() ? { surface: 'room' } : {}),
            ...this.notificationDeviceClaim(),
            // Malka Terp off the screen as it signs in (a VoIP push woke it):
            // offers keep ringing the phone instead of showing in the app.
            ...(this.claimsStandbyRing() ? { appState: this.appState } : {}),
            // anchorHold: this build can show the holding screen of an
            // interpreter's call-back (docs/interpreted-group-calls.md).
            // reconnectGrace: it reports the call its room is in
            // (call_room_entered, call_room_joined), so signing in alone is not
            // being back from a reconnect grace (vrs-server/ws/call-reconnect-grace.js).
            // callWaiting: a call ringing during this one shows as this build's
            // own call-waiting sheet (app) or overlay (room page), so the phone
            // gets no incoming-call push for it: on an iPhone that push rang
            // the system call screen beside the sheet (vrs-server/ws/p2p-handlers.js).
            // standbyRing: it rings an offer like a call while off the screen.
            // meetingRemote: Malka Terp takes an outside-meeting offer. The
            // meeting opens on the interpreter's web console (the desk), and
            // the phone reports its steps (docs/outside-meeting-desk.md).
            capabilities: {
                anchorHold: true,
                callWaiting: true,
                reconnectGrace: true,
                ...(this.claimsStandbyRing() ? { standbyRing: true } : {}),
                ...(this.claimsMeetingRemote() ? { meetingRemote: true } : {})
            }
        });
    }
    /** Malka Terp: an interpreter on a phone, with a web console desk for outside meetings. */
    claimsMeetingRemote() {
        return this.userRole === 'interpreter' && isNativeRuntime();
    }
    /** Malka Terp on a phone whose native side rings offers (configureStandbyRing). */
    claimsStandbyRing() {
        return this.userRole === 'interpreter' && this.standbyRingSupported && isNativeRuntime();
    }
    /**
     * Malka Terp's native call integration, on app mount: whether this build
     * rings a queue offer like a call while off the screen, and whether the
     * app is on screen now. Read by the next sign-in.
     */
    configureStandbyRing(supported, appState) {
        this.standbyRingSupported = supported;
        this.appState = appState;
    }
    /** Offers ring this phone while Malka Terp is off the screen: this build and the server both do it. */
    standbyRingActive() {
        return this.serverStandbyRing && this.claimsStandbyRing();
    }
    /**
     * Malka Terp came on or went off the screen. Off the screen while
     * Available, offers ring the phone instead of showing here; the next
     * sign-in carries the state if the socket is down now.
     */
    setAppState(appState) {
        if (this.appState === appState)
            return;
        this.appState = appState;
        if (!this.serverStandbyRing || !this.claimsStandbyRing() || !this.authenticated
            || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }
        this.send({ type: 'interpreter_app_state', data: { state: appState } });
    }
    /**
     * Signing out: tell the server before the socket closes. A phone that
     * rings offers while off the screen would otherwise stay in the queue
     * (the server also checks the session before every ring).
     */
    leaveQueueForSignOut() {
        this.lastSentInterpreterStatus = null;
        if (this.userRole !== 'interpreter' || !this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }
        this.send({ type: 'interpreter_status', data: { status: 'offline' } });
    }
    /**
     * Malka Terp names its notification registration, so an offer this socket
     * holds is pushed to this phone only, not to every device the interpreter
     * ever signed in on (vrs-server/ws/queue-notify.js offerPushAudience).
     */
    notificationDeviceClaim() {
        // Clients name it too: a ring that ends while this socket is open is
        // stopped here (ring_ended), so the server sends it no VoIP cancel.
        if ((this.userRole !== 'interpreter' && this.userRole !== 'client') || !isNativeRuntime())
            return {};
        const notificationDeviceId = getNotificationDeviceId();
        return notificationDeviceId ? { notificationDeviceId } : {};
    }
    /**
     * Recovers from a server-side auth rejection (expired token or revoked
     * session id) by minting a fresh access token via the refresh endpoint
     * and re-authenticating the live socket. Terminal credential failures
     * return native users to sign-in; non-auth errors remain visible in place.
     */
    async refreshAndReauthenticate(originalError) {
        const socket = this.ws;
        const identity = getApiSessionIdentity();
        const origin = getApiOrigin();
        const generation = getStorageGeneration();
        const result = await refreshAuthSession();
        const token = result.status === 'refreshed' ? result.token : undefined;
        if (generation !== getStorageGeneration() || !this.authReplyIsCurrent(socket, identity, origin, token)
            || result.status === 'superseded')
            return;
        if (result.status === 'refreshed') {
            mobileLog('info', 'queue_ws_auth_refreshed', {}, { console: false });
            this.sendAuthMessage(result.token);
            return;
        }
        if (result.status === 'unavailable') {
            this.authenticated = false;
            this.closeSocket();
            this.clearStabilityTimer();
            this.stopHeartbeat();
            this.emit('connection', { connected: false });
            this.emit('error', { code: 'AUTH_REFRESH_UNAVAILABLE', retrying: this.reconnectAttempts < this.maxReconnectAttempts,
                message: 'Your connection could not be renewed. Reconnecting…' });
            this.attemptReconnect();
            return;
        }
        if (result.status === 'rejected') {
            const { code } = result;
            const failure = { ...originalError, code };
            mobileLog('warn', 'queue_ws_auth_refresh_rejected', { code }, { console: false });
            this.shouldReconnect = false;
            const native = isNativeRuntime();
            if (native)
                this.emit('sessionRevoked', failure);
            try {
                vrsAuthService.logout();
            }
            catch {
                mobileLog('warn', 'queue_ws_terminal_auth_cleanup_failed', { code }, { console: false });
            }
            if (!native)
                this.emit('sessionRevoked', failure);
        }
    }
    registerNotificationDevice() {
        const storedToken = getStoredAuthToken();
        if (!storedToken?.token) {
            return;
        }
        void registerCurrentNotificationDevice().then(async (response) => {
            if (response.error) {
                mobileLog('warn', 'notification_device_registration_failed', {
                    status: response.status
                }, { console: false });
            }
            // iOS ordinary notifications (a client's meeting reminders, an
            // interpreter's offers) ride a second (alert) token; never prompts
            // here. A client's also carries silent ring cancels, which need no
            // permission, so it is registered even without one.
            if (isNativeRuntime()) {
                await (this.userRole === 'client' ? registerRingStopToken() : registerAlertNotificationToken(false));
            }
        }).catch(() => {
            mobileLog('warn', 'notification_device_registration_failed', {
                message: 'Notification device registration is not confirmed'
            }, { console: false });
        });
    }
    showBrowserNotification(data) {
        const windowRef = typeof window !== 'undefined'
            ? window
            : undefined;
        const NotificationRef = windowRef?.Notification;
        if (!windowRef || !NotificationRef || NotificationRef.permission !== 'granted') {
            return;
        }
        try {
            const notification = new NotificationRef(data.title || getAppName(), {
                body: data.body,
                data: data.data
            });
            notification.onclick = () => {
                windowRef.focus?.();
                const roomName = isRecord(data.data) ? stringValue(data.data.roomName) : undefined;
                if (roomName) {
                    const clientStartWithVideoMuted = isRecord(data.data)
                        && typeof data.data.clientStartWithVideoMuted === 'boolean'
                        ? data.data.clientStartWithVideoMuted
                        : undefined;
                    const startWithVideoMuted = getInterpretedStartWithVideoMuted(getUserRole(), clientStartWithVideoMuted);
                    windowRef.location.assign(`/${roomName}#config.startWithVideoMuted=${startWithVideoMuted}`);
                }
            };
        }
        catch {
            // Browser notification support is best-effort; server push remains authoritative.
        }
    }
    handleMessage(message) {
        switch (message.type) {
            case 'connected':
                this.userId = message.clientId || null;
                break;
            case 'auth_success': {
                const hadQueuedAvailability = this.pendingAuthedMessages.some(pending => pending.type === 'interpreter_status');
                this.authRefreshAttempted = false;
                this.authenticated = true;
                this.heldElsewhere = false;
                this.serverStandbyRing = this.userRole === 'interpreter' && isRecord(message.capabilities)
                    && message.capabilities.standbyRing === true;
                this.userId = message.userId
                    || getStoredJson('vrs_user_info')?.id
                    || this.userId;
                cachePublicNumber(message);
                this.flushPendingAuthedMessages();
                this.emit('authenticated', {
                    role: message.role,
                    clientId: message.clientId,
                    capabilities: isRecord(message.capabilities)
                        ? {
                            calendarReminders: message.capabilities.calendarReminders === true,
                            externalMeetingVrs: message.capabilities.externalMeetingVrs === true,
                            reconnectGraceMs: numberValue(message.capabilities.reconnectGraceMs),
                            interpretedAnchor: message.capabilities.interpretedAnchor === true,
                            phoneCaptions: message.capabilities.phoneCaptions === true,
                            standbyRing: message.capabilities.standbyRing === true,
                            meetingDesk: message.capabilities.meetingDesk === true,
                            watchAlerts: message.capabilities.watchAlerts === true
                        }
                        : undefined
                });
                this.registerNotificationDevice();
                if (this.userRole === 'interpreter'
                    && this.lastSentInterpreterStatus === 'active'
                    && !this.availabilityReplayHeld
                    && !hadQueuedAvailability
                    && !this.superseded
                    && !getStoredJson('vrs_active_call')?.roomName) {
                    const profile = getStoredJson('vrs_user_info');
                    this.sendInterpreterStatus('active', this.lastInterpreterName || profile?.name, this.lastInterpreterLanguages, this.lastInterpreterServiceModes || profile?.serviceModes);
                    mobileLog('info', 'queue_availability_restored_after_auth', {
                        languages: this.lastInterpreterLanguages
                    }, { console: false });
                }
                this.ping();
                break;
            }
            case 'public_number_changed':
                // An admin changed this interpreter's or captioner's public ID:
                // the next room shows it (providerDisplayName.ts).
                cachePublicNumber(message);
                break;
            case 'queue_status':
                this.emit('queueStatus', normalizeQueueStatus(message.data));
                break;
            case 'match_found':
                this.emit('matchFound', normalizeMatchPayload(message.data));
                break;
            case 'request_assigned':
                this.emit('requestAssigned', normalizeMatchPayload(message.data));
                break;
            case 'request_queued':
                this.emit('requestQueued', normalizeRequestQueued(message.data));
                break;
            case 'request_cancelled':
                this.emit('requestCancelled', normalizeRequestQueued(message.data));
                break;
            // An admin changed where this account may be served
            // (docs/regional-queue-routing.md): the cached account takes the
            // new grants so the region picker offers only what is granted now.
            case 'service_regions_updated': {
                const serviceRegions = cacheServiceRegions(recordPayload(message.data).serviceRegions);
                if (serviceRegions !== undefined) {
                    this.emit('serviceRegionsUpdated', { serviceRegions });
                }
                break;
            }
            // An interpreter request stored for later (request_interpreter
            // with schedule: true). reused: the account already had one; its
            // id and time come back instead (scheduledRequest.ts).
            case 'request_scheduled': {
                const data = recordPayload(message.data);
                this.emit('requestScheduled', {
                    callType: stringValue(data.callType) || undefined,
                    requestId: stringValue(data.requestId) || undefined,
                    reused: data.reused === true,
                    scheduledStartAt: stringValue(data.scheduledStartAt) || null
                });
                break;
            }
            /*
             * The interpreter called a client rather than waiting for one.
             *
             * Room entry is not handled here: the server sends
             * `meeting_initiated` immediately after the started event, and the
             * existing match path takes the interpreter into the room. These
             * two only carry what to say while that happens, and what to say
             * when it does not.
             */
            case 'interpreter_client_call_started': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('interpreterClientCallStarted', {
                    callId: String(payload.callId || ''),
                    clientName: String(payload.clientName || ''),
                    roomName: String(payload.roomName || '')
                });
                break;
            }
            case 'interpreter_client_call_failed': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('interpreterClientCallFailed', {
                    code: String(payload.code || ''),
                    message: String(payload.message || 'The client call could not be started.')
                });
                break;
            }
            // The request left the queue (client cancelled, timed out, or
            // another interpreter took it) — same dismissal path as an
            // explicit cancellation so delivered popups clear themselves.
            case 'request_withdrawn':
                this.emit('requestCancelled', normalizeRequestQueued(message.data));
                break;
            case 'interpreter_request':
                this.emit('interpreterRequest', normalizeInterpreterRequest(message.data));
                break;
            case 'request_accepted':
                this.emit('requestAccepted', normalizeMatchPayload(message.data));
                break;
            case 'request_declined':
                this.emit('requestDeclined', normalizeMatchPayload(message.data));
                break;
            case 'meeting_reminder':
                if (isRecord(message.data)) {
                    this.emit('meetingReminder', { reminders: Array.isArray(message.data.reminders) ? message.data.reminders : [] });
                }
                break;
            case 'external_meeting_progress':
                if (isRecord(message.data)) {
                    this.emit('externalMeetingProgress', {
                        callId: stringValue(message.data.callId),
                        event: message.data.event === 'entered' || message.data.event === 'identified'
                            ? message.data.event : undefined,
                        platform: stringValue(message.data.platform)
                    });
                }
                break;
            // Outside meetings (docs/outside-meeting-desk.md): the account's
            // web console desk, its meeting call, and each step the server
            // recorded. The meeting link never comes to Malka Terp.
            case 'meeting_desk_status': {
                const desk = meetingDeskStatusPayloadSchema.safeParse(message.data);
                if (desk.success) {
                    this.emit('meetingDeskStatus', desk.data);
                }
                else {
                    mobileLog('warn', 'queue_meeting_desk_status_unreadable', {}, { console: false });
                }
                break;
            }
            case 'external_meeting_call': {
                const call = externalMeetingCallPayloadSchema.safeParse(message.data);
                if (call.success) {
                    this.emit('externalMeetingCall', call.data);
                }
                else {
                    mobileLog('warn', 'queue_external_meeting_call_unreadable', {}, { console: false });
                }
                break;
            }
            case 'external_meeting_status_ack': {
                const ack = externalMeetingStatusAckPayloadSchema.safeParse(message.data);
                if (ack.success) {
                    this.emit('externalMeetingStatusAck', ack.data);
                }
                else {
                    mobileLog('warn', 'queue_external_meeting_ack_unreadable', {}, { console: false });
                }
                break;
            }
            case 'meeting_initiated':
                this.emit('meetingInitiated', normalizeMatchPayload(message.data));
                break;
            case 'watch_alert': {
                // A TV alert relayed from this account's web Home (docs/watch-alerts.md).
                const alert = watchAlertPayloadSchema.safeParse(message.data);
                if (alert.success) {
                    this.emit('watchAlert', alert.data);
                }
                break;
            }
            case 'call_ended':
                this.clearConferenceResponsesForCall(normalizeMatchPayload(message.data).callId);
                this.emit('callEnded', normalizeMatchPayload(message.data));
                break;
            case 'client_rering_sent':
                this.emit('clientReringSent', isRecord(message.data) ? message.data : {});
                break;
            case 'client_rering_skipped':
                // The client's calling browser already has this room open; a
                // ring would only reach the account's other devices.
                this.emit('clientReringSkipped', isRecord(message.data) ? message.data : {});
                break;
            case 'ping':
                this.send({ type: 'heartbeat' });
                break;
            case 'pong':
            case 'heartbeat_ack':
                break;
            case 'status_updated':
                // Out of the queue because this phone missed a ring or was away
                // too long (standby_*): Available again only when the
                // interpreter says so, not on the next reconnect.
                if (isRecord(message.data) && typeof message.data.reason === 'string'
                    && message.data.reason.startsWith('standby_')) {
                    this.lastSentInterpreterStatus = 'inactive';
                }
                this.emit('statusUpdated', isRecord(message.data) ? message.data : message);
                break;
            case 'session_superseded': {
                // The account signed in elsewhere and the server closed this
                // socket. Do NOT reconnect — otherwise the old and new tabs
                // would take turns superseding each other in an endless loop.
                const payload = isRecord(message.data) ? message.data : { message: 'Signed in on another device.' };
                this.shouldReconnect = false;
                this.superseded = true;
                mobileLog('warn', 'queue_ws_session_superseded', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('sessionSuperseded', payload);
                break;
            }
            case 'session_held_elsewhere': {
                // Not an auth failure and not a sign-out: this interpreter
                // account is in a live call on another device, so the server
                // holds this session out and keeps the socket open. Stay put
                // and let the user decide once that call ends.
                const payload = accountCallHoldPayload(message.data);
                this.heldElsewhere = true;
                this.authenticated = false;
                mobileLog('warn', 'queue_ws_session_held_elsewhere', {
                    role: payload.role,
                    recheckAfterMs: payload.recheckAfterMs
                }, { console: false });
                this.emit('sessionHeldElsewhere', payload);
                break;
            }
            case 'account_call_status': {
                const payload = accountCallHoldPayload(message.data);
                this.emit('accountCallStatus', { ...payload, held: isRecord(message.data) && message.data.held === true });
                break;
            }
            case 'call_active_elsewhere':
                this.emit('callActiveElsewhere', accountCallHoldPayload(message.data));
                break;
            case 'call_moving_elsewhere':
                this.emit('callMovingElsewhere', callMovePayload(message.data));
                break;
            case 'call_moved_elsewhere': {
                const payload = callMovePayload(message.data);
                if (payload.callId) {
                    this.movedAwayCallIds.add(payload.callId);
                }
                mobileLog('info', 'queue_call_moved_elsewhere', { hasCallId: Boolean(payload.callId) }, { console: false });
                this.emit('callMovedElsewhere', payload);
                break;
            }
            case 'call_move_lapsed':
                this.emit('callMoveLapsed', callMovePayload(message.data));
                break;
            // The reconnect grace (vrs-server/ws/call-reconnect-grace.js).
            case 'call_peer_reconnecting': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('peerReconnecting', {
                    callId: stringValue(payload.callId),
                    roomName: stringValue(payload.roomName) || null,
                    userId: stringValue(payload.userId),
                    role: stringValue(payload.role),
                    name: stringValue(payload.name) || null,
                    awaySince: stringValue(payload.awaySince),
                    graceEndsAt: stringValue(payload.graceEndsAt),
                    secondsLeft: numberValue(payload.secondsLeft),
                    cause: stringValue(payload.cause),
                    anchor: payload.anchor === true
                });
                break;
            }
            case 'call_peer_reconnected': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('peerReconnected', {
                    callId: stringValue(payload.callId),
                    roomName: stringValue(payload.roomName) || null,
                    userId: stringValue(payload.userId),
                    role: stringValue(payload.role),
                    awayMs: numberValue(payload.awayMs)
                });
                break;
            }
            case 'call_reconnect_available': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('callReconnectAvailable', {
                    callId: stringValue(payload.callId),
                    roomName: stringValue(payload.roomName) || null,
                    role: stringValue(payload.role),
                    callType: stringValue(payload.callType) || null,
                    graceEndsAt: stringValue(payload.graceEndsAt),
                    secondsLeft: numberValue(payload.secondsLeft)
                });
                break;
            }
            case 'call_reconnect_resolved': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('callReconnectResolved', {
                    callId: stringValue(payload.callId),
                    reason: stringValue(payload.reason)
                });
                break;
            }
            case 'call_end_ignored': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('callEndIgnored', {
                    callId: stringValue(payload.callId),
                    reason: stringValue(payload.reason),
                    message: stringValue(payload.message)
                });
                break;
            }
            case 'admin_force_logout': {
                // An admin kicked this account. Confirmed on device 2026-08-02:
                // the web surfaces signed out immediately while Android stayed
                // happily signed in, because nothing here handled the message
                // and the socket simply reconnected on its normal timer with a
                // still-valid access token.
                const payload = isRecord(message.data) ? message.data : {};
                this.shouldReconnect = false;
                mobileLog('warn', 'queue_ws_admin_force_logout', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                // Native listeners retire media and capture the original
                // credential before shared cleanup disconnects this socket.
                const native = isNativeRuntime();
                if (native)
                    this.emit('adminForceLogout', payload);
                try {
                    vrsAuthService.logout();
                }
                catch {
                    mobileLog('warn', 'queue_ws_admin_force_logout_cleanup_failed', {}, { console: false });
                }
                if (!native)
                    this.emit('adminForceLogout', payload);
                break;
            }
            case 'signed_out': {
                // "Sign out of all apps", or "Sign out" in another app or
                // browser of the same sign-in (vrs-server/lib/sign-out-all.js).
                // The server ended this session and closes the socket next.
                // The server's reason is never shown: the app says "Signed out".
                this.shouldReconnect = false;
                mobileLog('warn', 'queue_ws_signed_out', {}, { console: false });
                const native = isNativeRuntime();
                if (native)
                    this.emit('signedOut', {});
                try {
                    vrsAuthService.logout();
                }
                catch {
                    mobileLog('warn', 'queue_ws_signed_out_cleanup_failed', {}, { console: false });
                }
                if (!native)
                    this.emit('signedOut', {});
                break;
            }
            case 'auth_error': {
                const payload = isRecord(message.data) ? message.data : { message: 'Queue server error' };
                const code = typeof payload.code === 'string' ? payload.code : undefined;
                const refreshable = code === 'TOKEN_EXPIRED' || code === 'SESSION_REVOKED' || code === 'INVALID_TOKEN' || code === undefined;
                mobileLog('warn', 'queue_ws_auth_error', {
                    code,
                    refreshable,
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                // An expired/stale access token is recoverable: mint a fresh one
                // with the stored refresh token and re-authenticate, instead of
                // leaving the socket unauthenticated (which surfaces to users as
                // being kicked out / "request interpreter" failing).
                if (refreshable && !this.authRefreshAttempted) {
                    this.authRefreshAttempted = true;
                    void this.refreshAndReauthenticate(payload);
                    break;
                }
                this.emit('error', payload);
                break;
            }
            case 'error': {
                const payload = isRecord(message.data) ? message.data : { message: 'Queue server error' };
                if (payload.operation === 'phone_captions') {
                    const data = payload;
                    this.emit('phoneCaptionsState', {
                        callId: typeof data.callId === 'string' ? data.callId : undefined,
                        code: typeof data.code === 'string' ? data.code : undefined,
                        running: false
                    });
                }
                mobileLog('warn', 'queue_ws_server_error', {
                    code: typeof payload.code === 'string' ? payload.code : undefined,
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('error', payload);
                break;
            }
            // A message we sent failed the server's schema validation. Silently
            // dropping this turned bad payloads (e.g. a contact with a
            // malformed stored number in a group call) into dead taps.
            case 'validation_error': {
                const payload = isRecord(message.data) ? message.data : {};
                mobileLog('warn', 'queue_ws_validation_error', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('error', {
                    ...payload,
                    code: 'VALIDATION_ERROR',
                    message: typeof payload.message === 'string' && payload.message
                        ? payload.message
                        : 'The server rejected the request format.'
                });
                break;
            }
            // Handoff message types — emitted as events for device-handoff middleware
            case 'handoff_in_progress':
                this.emit('handoff_in_progress', message.data);
                break;
            case 'handoff_complete':
                this.emit('handoff_complete', message.data);
                break;
            case 'handoff_executed':
                this.emit('handoff_executed', message.data);
                break;
            case 'handoff_consumed':
                this.emit('handoff_consumed', message.data);
                break;
            case 'handoff_release':
                this.emit('handoff_release', message.data);
                break;
            case 'handoff_cancelled':
                this.emit('handoff_cancelled', message.data);
                break;
            case 'handoff_prepared':
                this.emit('handoff_prepared', message.data);
                break;
            case 'handoff_offer':
                this.emit('handoff_offer', message.data);
                break;
            case 'handoff_devices_updated':
                this.emit('handoff_devices_updated', message.data);
                break;
            case 'handoff_error':
                this.emit('handoff_error', message.data);
                break;
            case 'session_registered':
                this.emit('session_registered', message.data);
                break;
            case 'session_unregistered':
                this.emit('session_unregistered', message.data);
                break;
            // Call Management & UX
            case 'p2p_incoming_call':
                this.emit('p2pIncomingCall', normalizeP2PInvitePayload(message.data));
                break;
            case 'inbound_vrs_call':
                // A hearing party dialed this client's VRS number. Same
                // surfaces as a P2P ring: overlay + notification.
                this.emit('inboundVrsCall', recordPayload(message.data));
                break;
            case 'p2p_incoming_call_waiting': {
                const parsed = callWaitingIncomingSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit('callWaitingIncoming', parsed.data);
                }
                break;
            }
            case 'call_waiting_responded': {
                const parsed = callWaitingRespondedSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit('callWaitingResponded', parsed.data);
                }
                break;
            }
            // The host decides who joins a group call again
            // (vrs-server/ws/group-join-approval.js).
            case 'group_join_request':
            case 'group_join_resolved':
            case 'group_join_waiting': {
                const event = message.type === 'group_join_request' ? 'groupJoinRequest'
                    : message.type === 'group_join_resolved' ? 'groupJoinResolved' : 'groupJoinWaiting';
                const parsed = (message.type === 'group_join_request' ? groupJoinRequestSchema
                    : message.type === 'group_join_resolved' ? groupJoinResolvedSchema : groupJoinWaitingSchema)
                    .safeParse(message.data);
                if (parsed.success) {
                    this.emit(event, parsed.data);
                }
                break;
            }
            case 'call_on_hold':
            case 'call_off_hold': {
                const parsed = callHoldNoticeSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit(message.type === 'call_on_hold' ? 'callOnHold' : 'callOffHold', parsed.data);
                }
                break;
            }
            // Merge (Robert, 2026-10-12): a held call joined another.
            case 'call_merged': {
                const parsed = callMergedSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit('callMerged', parsed.data);
                }
                break;
            }
            case 'held_call_status': {
                const parsed = heldCallStatusSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit('heldCallStatus', parsed.data);
                }
                break;
            }
            case 'call_merge_done': {
                const parsed = callMergeDoneSchema.safeParse(message.data);
                if (parsed.success) {
                    this.emit('callMergeDone', parsed.data);
                }
                break;
            }
            case 'call_hold_updated':
                this.emit('callHoldUpdated', message.data);
                break;
            case 'call_transfer_initiated':
                this.emit('callTransferInitiated', message.data);
                break;
            case 'call_transfer_pending':
                this.emit('callTransferPending', message.data);
                break;
            case 'call_transfer_accepted':
                this.emit('callTransferAccepted', message.data);
                break;
            case 'call_transfer_cancelled':
                this.emit('callTransferCancelled', message.data);
                break;
            // Everyone in the call: a transfer's receiver is in and the
            // sender has left (vrs-server/ws/call-transfer-handover.js).
            case 'call_interpreter_changed': {
                const changed = callInterpreterChangedPayloadSchema.safeParse(message.data);
                if (changed.success) {
                    this.emit('callInterpreterChanged', changed.data);
                }
                break;
            }
            // A transfer's phone leg: moved to this receiver on a retry, or
            // left with the sender (vrs-server/ws/call-transfer-handover.js).
            case 'call_phone_leg_handed_over': {
                const moved = callPhoneLegHandedOverPayloadSchema.safeParse(message.data);
                if (moved.success) {
                    this.emit('callPhoneLegHandedOver', moved.data);
                }
                break;
            }
            case 'call_transfer_phone_leg_failed': {
                const kept = callTransferPhoneLegFailedPayloadSchema.safeParse(message.data);
                if (kept.success) {
                    this.emit('callTransferPhoneLegFailed', kept.data);
                }
                break;
            }
            // A colleague asks this interpreter to take over their call.
            // Used to fall through to "Unknown queue message type", so Malka
            // Terp never saw a warm transfer (transferOffers.ts).
            case 'call_transfer_offer': {
                const offer = callTransferOfferPayloadSchema.safeParse(message.data);
                if (!offer.success) {
                    mobileLog('warn', 'call_transfer_offer_invalid', {}, { console: false });
                    break;
                }
                this.emit('callTransferOffer', offer.data);
                break;
            }
            // That offer is over: the call ended or its sender withdrew it.
            case 'call_transfer_offer_ended': {
                const ended = callTransferOfferEndedPayloadSchema.safeParse(message.data);
                if (!ended.success) {
                    mobileLog('warn', 'call_transfer_offer_ended_invalid', {}, { console: false });
                    break;
                }
                this.emit('callTransferOfferEnded', ended.data);
                break;
            }
            // In-call team interpreting (vrs-server/ws/interpreter-teams.js).
            case 'team_offer': {
                const offer = teamOfferPayloadSchema.safeParse(message.data);
                if (!offer.success) {
                    mobileLog('warn', 'team_offer_invalid', {}, { console: false });
                    break;
                }
                this.emit('teamOffer', offer.data);
                break;
            }
            case 'team_offer_ended': {
                const ended = teamOfferEndedPayloadSchema.safeParse(message.data);
                if (ended.success)
                    this.emit('teamOfferEnded', ended.data);
                break;
            }
            case 'team_joined': {
                const joined = teamJoinedPayloadSchema.safeParse(message.data);
                if (!joined.success) {
                    mobileLog('warn', 'team_joined_invalid', {}, { console: false });
                    break;
                }
                this.emit('teamJoined', joined.data);
                break;
            }
            case 'team_request_pending':
            case 'team_accepted':
            case 'team_request_ended':
            case 'team_ended': {
                const update = teamStatePayloadSchema.safeParse(message.data);
                if (!update.success)
                    break;
                this.emit({
                    team_request_pending: 'teamRequestPending',
                    team_accepted: 'teamAccepted',
                    team_request_ended: 'teamRequestEnded',
                    team_ended: 'teamEnded'
                }[message.type], update.data);
                break;
            }
            case 'conference_invite':
                this.emit('p2pIncomingCall', normalizeP2PInvitePayload(message.data));
                this.emit('conferenceInvite', message.data);
                break;
            case 'conference_add_ringing':
                this.emit('conferenceAddRinging', message.data);
                break;
            // In-call invite outcomes for the inviter (web InvitePanel rows).
            case 'conference_invite_sent': {
                const data = recordPayload(message.data);
                this.emit('conferenceInviteSent', {
                    ...data,
                    callId: stringValue(data.callId),
                    expiresAt: stringValue(data.expiresAt),
                    inviteId: stringValue(data.inviteId),
                    requested: stringValue(data.requested),
                    ringing: data.ringing === true,
                    target: data.target
                });
                break;
            }
            case 'conference_invite_result': {
                const data = recordPayload(message.data);
                this.emit('conferenceInviteResult', {
                    ...data,
                    callId: stringValue(data.callId),
                    inviteId: stringValue(data.inviteId),
                    name: stringValue(data.name),
                    status: data.status === 'accepted' || data.status === 'declined' || data.status === 'missed'
                        || data.status === 'cancelled'
                        ? data.status : undefined,
                    targetId: stringValue(data.targetId)
                });
                break;
            }
            case 'p2p_group_no_answer': {
                const data = recordPayload(message.data);
                this.emit('p2pGroupNoAnswer', {
                    callId: stringValue(data.callId),
                    declined: normalizeCallPeers(data.declined),
                    missed: normalizeCallPeers(data.missed),
                    roomName: stringValue(data.roomName)
                });
                break;
            }
            // A live call with only this client (the instant meeting it asked
            // for); the middleware enters its room.
            case 'instant_meeting_started': {
                const data = recordPayload(message.data);
                const callId = stringValue(data.callId);
                const clientId = stringValue(data.clientId);
                const roomName = stringValue(data.roomName);
                if (callId && clientId && roomName) {
                    this.emit('instantMeetingStarted', {
                        callId,
                        clientId,
                        requestId: stringValue(data.requestId),
                        roomName
                    });
                }
                break;
            }
            // The unseen missed calls (sent on sign-in and when a ring is
            // missed): VRS Home's Now list. A row that does not parse is left
            // out, never guessed at.
            case 'missed_calls': {
                const rows = Array.isArray(message.data) ? message.data : [];
                this.emit('missedCalls', {
                    calls: rows.flatMap(row => {
                        const parsed = missedCallSchema.safeParse(row);
                        return parsed.success ? [parsed.data] : [];
                    })
                });
                break;
            }
            // A missed group ring whose call is still live: the invitee may
            // still join by accepting again (VRS Home).
            case 'p2p_room_still_active': {
                const data = recordPayload(message.data);
                this.emit('p2pRoomStillActive', {
                    callId: stringValue(data.callId),
                    callerId: stringValue(data.callerId),
                    callerName: stringValue(data.callerName),
                    groupCall: data.groupCall === true,
                    participantCount: numberValue(data.participantCount),
                    roomName: stringValue(data.roomName)
                });
                break;
            }
            case 'p2p_participant_joined':
            case 'p2p_participant_left': {
                const data = recordPayload(message.data);
                this.emit(message.type === 'p2p_participant_joined' ? 'p2pParticipantJoined' : 'p2pParticipantLeft', {
                    callId: stringValue(data.callId),
                    hostId: stringValue(data.hostId),
                    name: stringValue(data.name),
                    participantCount: numberValue(data.participantCount),
                    rejoined: data.rejoined === true,
                    removed: data.removed === true,
                    userId: stringValue(data.userId)
                });
                break;
            }
            case 'conference_removed':
                this.clearConferenceResponsesForCall(normalizeMatchPayload(message.data).callId);
                this.emit('conferenceRemoved', message.data);
                this.emit('callEnded', normalizeMatchPayload(message.data));
                break;
            case 'conference_participant_removed':
                this.emit('conferenceParticipantRemoved', message.data);
                break;
            case 'chat_message':
                this.emit('chatMessage', message.data);
                break;
            case 'chat_message_sent':
                this.emit('chatMessageSent', message.data);
                break;
            case 'chat_history':
                this.emit('chatHistory', message.data);
                break;
            case 'preferences_updated':
                this.emit('preferencesUpdated', message.data);
                break;
            case 'p2p_target_dnd':
                this.emit('p2pTargetDnd', recordPayload(message.data));
                break;
            case 'p2p_accepted':
                this.emit('p2pAccepted', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_declined':
                this.emit('p2pDeclined', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_call_cancelled':
                this.emit('p2pCallCancelled', recordPayload(message.data));
                break;
            // A ring on this person's devices is over, however it ended
            // (vrs-server/ws/ring-ledger.js): sheets close, and the phone's
            // system ring stops (mobile/call-integration/ringEnd.ts).
            case 'ring_ended': {
                const ended = normalizeRingEndedPayload(message.data);
                if (ended)
                    this.emit('ringEnded', ended);
                break;
            }
            case 'p2p_call_ended':
                this.emit('p2pCallEnded', normalizeP2PInvitePayload(message.data));
                break;
            case 'conference_join_room': {
                const invitation = normalizeP2PInvitePayload(message.data);
                if (invitation.callId && this.conferenceResponses.get(`${invitation.callId}:${invitation.invitationId}`) === true) {
                    this.clearConferenceResponseTimer(`${invitation.callId}:${invitation.invitationId}`);
                    this.conferenceResponses.delete(`${invitation.callId}:${invitation.invitationId}`);
                    this.emit('p2pJoinRoom', invitation);
                }
                break;
            }
            case 'conference_invitation_error': {
                const data = recordPayload(message.data);
                const key = `${data.callId}:${data.invitationId}`;
                this.clearConferenceResponseTimer(key);
                if (data.code !== 'INVITATION_FAILED')
                    this.conferenceResponses.delete(key);
                this.emit('conferenceInvitationError', {
                    invitationId: typeof data.invitationId === 'string' ? data.invitationId : undefined,
                    callId: typeof data.callId === 'string' ? data.callId : undefined,
                    code: typeof data.code === 'string' ? data.code : undefined,
                    message: typeof data.message === 'string' ? data.message : undefined
                });
                break;
            }
            case 'conference_invitation_update': {
                const data = recordPayload(message.data);
                // This device still needs the canonical room acknowledgement. An account-wide
                // accepted update must not dismiss its retry UI if that acknowledgement was lost.
                if (data.status === 'accepted' && this.conferenceResponses.get(`${data.callId}:${data.invitationId}`) === true)
                    break;
                this.emit('conferenceInvitationUpdate', {
                    invitationId: typeof data.invitationId === 'string' ? data.invitationId : undefined,
                    callId: typeof data.callId === 'string' ? data.callId : undefined,
                    participantId: typeof data.participantId === 'string' ? data.participantId : undefined,
                    status: data.status === 'accepted' || data.status === 'declined' || data.status === 'unavailable'
                        || data.status === 'expired' ? data.status : undefined
                });
                break;
            }
            case 'p2p_join_room':
                this.emit('p2pJoinRoom', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_ringing': {
                const ringing = isRecord(message.data) ? message.data : {};
                const delivery = ringing.delivery;
                this.emit('p2pRinging', {
                    ...normalizeP2PInvitePayload(message.data),
                    calleeId: stringValue(ringing.calleeId),
                    calleeName: stringValue(ringing.calleeName),
                    calleePhotoUrl: profilePhotoPath(ringing.calleePhotoUrl),
                    delivery: delivery === 'socket' || delivery === 'push' || delivery === 'unconfirmed'
                        ? delivery
                        : undefined,
                    deliveryReason: stringValue(ringing.deliveryReason),
                    dnd: normalizeCallPeers(ringing.dnd),
                    invited: normalizeCallPeers(ringing.invited),
                    offline: normalizeCallPeers(ringing.offline),
                    unresolved: Array.isArray(ringing.unresolved) ? ringing.unresolved : undefined
                });
                break;
            }
            case 'p2p_call_failed':
                this.emit('p2pCallFailed', recordPayload(message.data));
                break;
            case 'p2p_interpreter_requested': {
                // The contract requires callId (contracts/queue.ts); the server sends
                // { callId, requestId }. Reject a payload without it rather than
                // emitting an event the listeners cannot act on.
                const data = recordPayload(message.data);
                if (typeof data.callId !== 'string' || !data.callId) {
                    console.warn('p2p_interpreter_requested without callId:', message);
                    break;
                }
                this.emit('p2pInterpreterRequested', { ...data, callId: data.callId });
                break;
            }
            // Captions of this interpreter's phone call
            // (react/features/interpreter-phone/phoneCaptions.ts).
            case 'phone_caption': {
                const data = recordPayload(message.data);
                const track = data.track;
                if (typeof data.callId === 'string' && typeof data.text === 'string' && data.text.trim()
                    && typeof data.segmentId === 'string'
                    && (track === 'hearing' || track === 'interpreter' || track === 'deaf')) {
                    this.emit('phoneCaption', {
                        callId: data.callId,
                        confidence: typeof data.confidence === 'number' ? data.confidence : null,
                        isFinal: data.isFinal === true,
                        segmentId: data.segmentId,
                        text: data.text,
                        track
                    });
                }
                break;
            }
            case 'phone_captions_started':
            case 'phone_captions_stopped': {
                const data = recordPayload(message.data);
                this.emit('phoneCaptionsState', {
                    callId: stringValue(data.callId),
                    running: message.type === 'phone_captions_started'
                });
                break;
            }
            case 'p2p_interpreter_joined':
                this.emit('p2pInterpreterJoined', recordPayload(message.data));
                break;
            // The anchor of an interpreted group call left; the call goes on
            // with the next Deaf person as the anchor (vrs-server
            // p2p-handlers moveAnchorOnDeparture).
            case 'p2p_anchor_changed':
                this.emit('p2pAnchorChanged', recordPayload(message.data));
                break;
            case 'p2p_interpreter_unavailable':
                this.emit('p2pInterpreterUnavailable', recordPayload(message.data));
                break;
            // Interpreted calls tied to the person who asked for the
            // interpreter (docs/interpreted-group-calls.md).
            case 'p2p_interpreter_left':
                this.emit('p2pInterpreterLeft', recordPayload(message.data));
                break;
            case 'anchor_hold':
                this.emit('anchorHold', recordPayload(message.data));
                break;
            case 'anchor_hold_update':
                this.emit('anchorHoldUpdate', recordPayload(message.data));
                break;
            case 'anchor_hold_released':
                this.emit('anchorHoldReleased', recordPayload(message.data));
                break;
            case 'anchor_hold_ended':
                this.emit('anchorHoldEnded', recordPayload(message.data));
                break;
            case 'interpreter_callback_status':
                this.emit('interpreterCallbackStatus', recordPayload(message.data));
                break;
            case 'p2p_target_offline':
                this.emit('p2pTargetOffline', recordPayload(message.data));
                this.emit('p2p_target_offline', recordPayload(message.data));
                break;
            case 'vrs_client_unavailable':
                this.emit('vrsClientUnavailable', recordPayload(message.data));
                break;
            case 'vrs_client_reconnecting':
                this.emit('vrsClientReconnecting', recordPayload(message.data));
                break;
            case 'notification_event':
                {
                    const payload = normalizeNotificationPayload(message.data);
                    this.showBrowserNotification(payload);
                    this.emit('notificationEvent', payload);
                }
                break;
            case 'voicemail_new_message':
                this.emit('voicemail_new_message', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_unread_count':
                this.emit('voicemail_unread_count', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_started':
                this.emit('voicemail_recording_started', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_complete':
                this.emit('voicemail_recording_complete', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_cancelled':
                this.emit('voicemail_recording_cancelled', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_message_deleted':
                this.emit('voicemail_message_deleted', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_error':
                this.emit('voicemail_error', normalizeVoicemailPayload(message.data));
                break;
            case 'vri_invite_prepared':
                this.emit('vriInvitePrepared', normalizeVriInvitePrepared(message.data));
                break;
            case 'contacts_changed':
                this.emit('contactsChanged', message.data);
                break;
            default:
                console.warn('Unknown queue message type:', message.type, message);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    off(event, callback) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            const index = eventListeners.indexOf(callback);
            if (index > -1) {
                eventListeners.splice(index, 1);
            }
        }
    }
    /**
     * Locally announce a room join through the same event the server's
     * p2p_join_room message would fire. Used by the inbound-VRS answer path,
     * where there is no p2p call to accept — the join is client-initiated.
     */
    announceLocalRoomJoin(data) {
        this.emit('p2pJoinRoom', data);
    }
    emit(event, data) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            [...eventListeners].forEach(callback => {
                try {
                    callback(data);
                }
                catch (error) {
                    console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }
    updateInterpreterStatus(status, name, languages = getDefaultInterpreterLanguages(), serviceModes) {
        // A deliberate availability change is explicit user intent to own the
        // queue session in THIS tab — the one case that may take the session
        // back from whichever tab superseded it.
        if (this.superseded) {
            this.superseded = false;
            this.shouldReconnect = true;
            this.reconnectAttempts = 0;
        }
        this.availabilityReplayHeld = false;
        this.lastSentInterpreterStatus = status;
        this.lastInterpreterName = name || this.lastInterpreterName;
        this.lastInterpreterLanguages = languages;
        this.lastInterpreterServiceModes = serviceModes;
        this.sendInterpreterStatus(status, name, languages, serviceModes);
    }
    sendInterpreterStatus(status, name, languages = getDefaultInterpreterLanguages(), serviceModes) {
        this.sendWhenAuthenticated({
            type: 'interpreter_status',
            data: {
                status,
                name,
                languages,
                serviceModes
            }
        });
    }
    /**
     * After a call the server leaves the interpreter out of the queue until
     * this client says otherwise. While the wrap-up decides when that is, a
     * reconnect must not replay the Available they held before the call. Any
     * explicit availability change (updateInterpreterStatus) ends the hold.
     */
    holdAvailabilityReplay() {
        this.availabilityReplayHeld = true;
    }
    /**
     * The last availability this client asked the server for. Lets UI that
     * mounts later (e.g. the in-call status toggle) reflect a choice made
     * elsewhere, such as going active on the dashboard.
     */
    getLastInterpreterStatus() {
        return this.lastSentInterpreterStatus;
    }
    requestInterpreter(language = getDefaultRequestLanguage(), clientName, roomNameOrOptions) {
        const options = typeof roomNameOrOptions === 'string'
            ? { roomName: roomNameOrOptions }
            : roomNameOrOptions || {};
        mobileLog('info', 'queue_request_interpreter_send_attempt', {
            authenticated: this.authenticated,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: this.isConnected(),
            wsReadyState: this.ws?.readyState ?? null
        }, { console: false });
        // Matches broadcast to every signed-in device of this account; only
        // the device that placed the request may auto-enter the room. Others
        // ring instead of silently hijacking the call (the phone-vs-laptop
        // supersede war that flashed cameras on and off).
        setPersistentItem('vrs_own_queue_request', JSON.stringify({ at: Date.now() }));
        this.sendWhenAuthenticated({
            type: 'request_interpreter',
            data: {
                callType: options.callType,
                callerIdPhone: options.callerIdPhone,
                corporateAccountId: options.corporateAccountId,
                costCenter: options.costCenter,
                department: options.department,
                industryTag: options.industryTag,
                instructions: options.instructions,
                inviteTokens: options.inviteTokens,
                participants: options.participants,
                language,
                clientName,
                roomName: options.roomName || getCurrentRoomName(),
                scheduledSessionId: options.scheduledSessionId,
                scheduledStartAt: options.scheduledStartAt,
                targetPhone: options.targetPhone,
                vcoEnabled: options.vcoEnabled || undefined,
                // Where the call is served (docs/regional-queue-routing.md).
                serviceRegion: options.serviceRegion || undefined,
                externalMeetingUrl: options.externalMeetingUrl || undefined,
                externalMeetingReminderId: options.externalMeetingReminderId || undefined
            }
        });
    }
    /**
     * Store an interpreter request for later (request_interpreter with
     * schedule: true): the server answers request_scheduled, or an error with
     * operation 'request_interpreter'. One scheduled request per account; a
     * second returns the first (reused). Unlike requestInterpreter it opens no
     * room and marks no request as this device's own: nothing is matched now.
     * Cancelled with cancelRequest(requestId).
     */
    scheduleInterpreterRequest(scheduledStartAt, options = {}) {
        this.sendWhenAuthenticated({
            type: 'request_interpreter',
            data: {
                callType: options.callType || 'vrs',
                callerIdPhone: options.callerIdPhone || undefined,
                clientName: options.clientName || undefined,
                language: options.language || getDefaultRequestLanguage(),
                schedule: true,
                scheduledStartAt,
                serviceRegion: options.serviceRegion || undefined
            }
        });
    }
    cancelRequest(requestId) {
        this.sendWhenAuthenticated({
            type: 'cancel_request',
            data: { requestId }
        });
    }
    acceptRequest(requestId, roomName, offerId) {
        this.sendWhenAuthenticated({
            type: 'accept_request',
            data: { requestId, roomName, offerId }
        });
    }
    declineRequest(requestId, offerId) {
        this.sendWhenAuthenticated({
            type: 'decline_request',
            data: { requestId, offerId }
        });
    }
    /** Take over a colleague's call (their `call_transfer_offer`). */
    acceptCallTransfer(transferId) {
        this.sendWhenAuthenticated({
            type: 'call_transfer_accept',
            data: { transferId }
        });
    }
    /** Leave the call with the colleague; acknowledged by `call_transfer_cancelled`. */
    declineCallTransfer(transferId) {
        this.sendWhenAuthenticated({
            type: 'call_transfer_cancel',
            data: { transferId }
        });
    }
    /**
     * The sender takes back their own pending transfer; acknowledged by
     * `call_transfer_cancelled { reason: 'withdrawn' }`, or refused with
     * TRANSFER_ALREADY_ACCEPTED when the colleague got there first.
     */
    withdrawCallTransfer(transferId) {
        this.sendWhenAuthenticated({
            type: 'call_transfer_cancel',
            data: { transferId }
        });
    }
    /**
     * Ask a colleague to join this call as its team interpreter: one named
     * available colleague, or without `toInterpreterId` every eligible one
     * (the transfer picker's list). Answered by `team_request_pending`.
     */
    requestTeam(callId, toInterpreterId) {
        this.sendWhenAuthenticated({
            type: 'team_request',
            data: toInterpreterId ? { callId, toInterpreterId } : { callId }
        });
    }
    /** Join a colleague's call as their teammate (their `team_offer`). */
    acceptTeam(teamId) {
        this.sendWhenAuthenticated({ type: 'team_accept', data: { teamId } });
    }
    declineTeam(teamId) {
        this.sendWhenAuthenticated({ type: 'team_decline', data: { teamId } });
    }
    /** Withdraw this interpreter's request before anyone accepts. */
    cancelTeamRequest(teamId) {
        this.sendWhenAuthenticated({ type: 'team_cancel', data: { teamId } });
    }
    /** The teammate leaves the call, or the primary releases them; the call goes on. */
    leaveTeam(teamId) {
        this.sendWhenAuthenticated({ type: 'team_leave', data: { teamId } });
    }
    transferCall(callId, toInterpreterId, reason) {
        this.sendWhenAuthenticated({
            type: 'call_transfer',
            data: { callId, toInterpreterId, transferType: 'blind', reason: reason || 'In-call transfer' }
        });
    }
    /**
     * Re-ring the deaf client bound to this call (inbound calls where the
     * auto-ring was missed). The server resolves the target from the call
     * record — no other targets are possible.
     */
    ringClientAgain(callId) {
        this.sendWhenAuthenticated({
            type: 'ring_client_again',
            data: { callId }
        });
    }
    /** Mint an expiring guest link for the room this client is live in. */
    prepareRoomInvite(roomName, requestId, opts) {
        this.sendWhenAuthenticated({
            type: 'prepare_vri_invite',
            data: {
                roomName,
                requestId,
                ...(opts?.guestEmail ? { guestEmail: opts.guestEmail } : {}),
                ...(opts?.deliveryChannels?.length ? { deliveryChannels: opts.deliveryChannels } : {})
            }
        });
    }
    /**
     * Ring someone into a live call (client→client only). Any joined
     * participant may invite, by saved account, number, @handle or email;
     * the answer is conference_invite_sent or an error with operation
     * 'conference_add'.
     */
    conferenceAdd(callId, target) {
        this.sendWhenAuthenticated({
            type: 'conference_add',
            data: { callId, ...target }
        });
    }
    sendP2PCall(target) {
        const phoneNumber = normalizeP2PPhoneTarget(target);
        this.sendWhenAuthenticated({
            type: 'p2p_call',
            data: phoneNumber ? { phoneNumber } : { target }
        });
    }
    /**
     * In-call green button: request an interpreter into the live P2P room, in
     * the caller's sign language. P2P calls record none, so without it the
     * server falls back to ASL.
     */
    sendP2PRequestInterpreter(callId, roomName, requestId, target = {}) {
        const data = {
            callId,
            roomName,
            requestId
        };
        if (target.language) {
            data.language = target.language;
        }
        if (target.serviceRegion) {
            data.serviceRegion = target.serviceRegion;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_request_interpreter',
            data
        });
    }
    /**
     * Call a client, from the interpreter side.
     *
     * The mirror of a client requesting an interpreter, and the one interpreter
     * workflow that existed on web and nowhere else — Malka Terp could answer a
     * call but never place one, so an interpreter who needed to reconnect with
     * a client had to go and find a browser.
     *
     * The server resolves the target against client accounts only, by public
     * handle or assigned VRS number, and fails closed for busy, DND or
     * unreachable. One field either way: it accepts a handle or a number and
     * works out which it is.
     */
    sendInterpreterCallClient(target) {
        this.sendWhenAuthenticated({
            type: 'interpreter_call_client',
            data: { target: target.trim() }
        });
    }
    sendGroupCall(participants, roomName) {
        this.sendWhenAuthenticated({
            type: 'group_call',
            data: { participants, roomName }
        });
    }
    clearConferenceResponseTimer(key) {
        const timer = this.conferenceResponseTimers.get(key);
        if (timer)
            clearTimeout(timer);
        this.conferenceResponseTimers.delete(key);
    }
    clearConferenceResponsesForCall(callId) {
        if (!callId)
            return;
        for (const key of this.conferenceResponses.keys()) {
            if (key.startsWith(`${callId}:`)) {
                this.clearConferenceResponseTimer(key);
                this.conferenceResponses.delete(key);
            }
        }
        this.pendingAuthedMessages = this.pendingAuthedMessages.filter(message => message.type !== 'conference_respond' || recordPayload(message.data).callId !== callId);
    }
    rememberConferenceResponse(callId, invitationId, accept) {
        const key = `${callId}:${invitationId}`;
        this.clearConferenceResponseTimer(key);
        this.conferenceResponses.set(key, accept);
        // A newer response supersedes an unsent one after a connection loss.
        this.pendingAuthedMessages = this.pendingAuthedMessages.filter(message => message.type !== 'conference_respond' || recordPayload(message.data).callId !== callId
            || recordPayload(message.data).invitationId !== invitationId);
        if (accept) {
            this.conferenceResponseTimers.set(key, setTimeout(() => {
                this.conferenceResponseTimers.delete(key);
                this.emit('conferenceInvitationError', { callId, invitationId, code: 'RESPONSE_TIMEOUT',
                    message: 'Answer not confirmed. Try again.' });
            }, 10000));
        }
        if (this.conferenceResponses.size > 64) {
            const oldest = this.conferenceResponses.keys().next().value;
            if (oldest) {
                this.clearConferenceResponseTimer(oldest);
                this.conferenceResponses.delete(oldest);
            }
        }
    }
    // callerId is a hint: the server answers from the call row, and an answer
    // from Android's system ring screen does not know the caller. endCallId:
    // "End & answer" from inside another call names the call being left, so
    // an invitation's busy check passes over it (ws/conference-invitations.js).
    acceptP2PCall({ callId, callerId, roomName, conferenceInvitation, invitationId, endCallId }) {
        if (conferenceInvitation) {
            if (!invitationId)
                throw new Error('This invitation is incomplete.');
            this.rememberConferenceResponse(callId, invitationId, true);
            try {
                this.sendWhenAuthenticated({ type: 'conference_respond', data: { callId, invitationId, accept: true,
                        ...endCallId ? { endCallId } : {} } });
            }
            catch (error) {
                this.clearConferenceResponseTimer(`${callId}:${invitationId}`);
                throw error;
            }
            return;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_accept',
            data: { callId, ...callerId ? { callerId } : {}, roomName }
        });
    }
    /**
     * The host's answer to someone asking to join their group call again
     * (group_join_request; vrs-server/ws/group-join-approval.js).
     */
    respondToGroupJoin(callId, requestId, accept) {
        this.sendWhenAuthenticated({ type: 'group_join_respond', data: { callId, requestId, accept } });
    }
    /**
     * Answer a call that rang while this client is in another one
     * (p2p_incoming_call_waiting). The server answers with
     * call_waiting_responded, and for an accept with p2p_join_room.
     */
    respondToCallWaiting({ action, currentCallId, incomingCallId }) {
        if (!incomingCallId || !currentCallId) {
            throw new Error('This call can no longer be answered.');
        }
        this.sendWhenAuthenticated({
            type: 'call_waiting_respond',
            data: { action, currentCallId, incomingCallId }
        });
    }
    /**
     * Asks whether the call this device kept on hold is still live (held_call_status).
     *
     * @param {string} callId - The held call.
     * @returns {void}
     */
    checkHeldCall(callId) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'held_call_status',
            data: { callId }
        });
    }
    /**
     * Merge the call this person holds into the one they are in (call_merge).
     *
     * @param {string} heldCallId - The held call.
     * @param {string} intoCallId - The call this person is in.
     * @returns {void}
     */
    mergeHeldCall(heldCallId, intoCallId) {
        if (!heldCallId || !intoCallId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_merge',
            data: {
                heldCallId,
                intoCallId
            }
        });
    }
    /** Put a call this person is in on hold, or take it off (call_hold). */
    setCallHold(callId, onHold) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_hold',
            data: { callId, onHold }
        });
    }
    declineP2PCall({ callId, callerId, sendToVoicemail, conferenceInvitation, invitationId, reason }) {
        if (conferenceInvitation) {
            if (!invitationId)
                throw new Error('This invitation is incomplete.');
            const cancelPendingAcceptance = this.conferenceResponses.get(`${callId}:${invitationId}`) === true;
            this.rememberConferenceResponse(callId, invitationId, false);
            this.sendWhenAuthenticated({ type: 'conference_respond', data: { callId, invitationId, accept: false, cancelPendingAcceptance } });
            return;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_decline',
            data: reason ? { callId, callerId, sendToVoicemail, reason } : { callId, callerId, sendToVoicemail }
        });
    }
    prepareVriInvite(data) {
        this.sendWhenAuthenticated({
            type: 'prepare_vri_invite',
            data
        });
    }
    registerHandoffSession(roomName, deviceId) {
        // Handoff sessions belong to clients; interpreters only receive transfer notices.
        if (this.userRole !== 'client' || !roomName || !deviceId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'session_register',
            data: {
                roomName,
                deviceId
            }
        });
    }
    unregisterHandoffSession() {
        if (this.userRole !== 'client') {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'session_unregister',
            data: {
                deviceId: getHandoffDeviceId()
            }
        });
    }
    /**
     * Whether this call moved to another device of this client account. A
     * device the call moved away from leaves without ending it.
     */
    hasCallMovedAway(callId) {
        return Boolean(callId && this.movedAwayCallIds.has(callId));
    }
    /** This device is taking the call back ("Move it back here"): it may end it again. */
    forgetCallMovedAway(callId) {
        this.movedAwayCallIds.delete(callId);
    }
    /**
     * One step of this interpreter's outside-meeting call
     * (external_meeting_status, docs/outside-meeting-desk.md): 'entered',
     * 'identified' (VRS only), or 'ended' with the end reason. The server
     * answers external_meeting_status_ack, or call_ended after 'ended'.
     */
    sendExternalMeetingStatus(callId, event, endReason) {
        this.sendWhenAuthenticated({
            type: 'external_meeting_status',
            data: { callId, event, ...(event === 'ended' && endReason ? { endReason } : {}) }
        });
    }
    /**
     * Ends a call that has no local room record, e.g. an external-meeting call,
     * or leaves a call-back's holding screen. `departure` says whether this is
     * a hang-up (the default) or the page going away: for the person an
     * interpreter is tied to, a page exit is a short grace, not the end.
     */
    endCall(callId, roomName, departure) {
        if (this.hasCallMovedAway(callId)) {
            mobileLog('info', 'queue_call_end_suppressed_moved_away', {}, { console: false });
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_end',
            data: { callId, ...(roomName ? { roomName } : {}), ...(departure ? { departure } : {}) }
        });
    }
    /**
     * End it and call (activeCallGate): ends an open call this device is not
     * in, as this person's hang-up would. The person confirmed it with that
     * tap, so a call moved to another device of the account ends too
     * (confirmedEnd passes the server's moved-call guard).
     */
    endOpenCall(callId, roomName) {
        if (!callId) {
            return;
        }
        this.movedAwayCallIds.delete(callId);
        this.sendWhenAuthenticated({
            type: 'call_end',
            data: { callId, ...(roomName ? { roomName } : {}), departure: 'hangup', confirmedEnd: true }
        });
    }
    /**
     * The interpreter calls back after the person they were with dropped
     * (interpreter_callback). Who is rung comes from the server's record.
     */
    requestInterpreterCallback(callId) {
        this.sendWhenAuthenticated({ type: 'interpreter_callback', data: { callId } });
    }
    /**
     * Start or stop the captions of this interpreter's phone call
     * (phone_captions_start / phone_captions_stop; vrs-server
     * ws/phone-caption-handlers.js). Sent only on a live, authenticated
     * socket: phoneCaptions.ts asks again after a reconnect.
     *
     * @returns {boolean} Whether the request went out.
     */
    setPhoneCaptions(callId, on) {
        const type = on ? 'phone_captions_start' : 'phone_captions_stop';
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: type, reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type, data: { callId } }));
        return true;
    }
    /** The interpreter cancels a call-back still waiting for the anchor. */
    cancelInterpreterCallback(callbackCallId) {
        this.sendWhenAuthenticated({ type: 'interpreter_callback_cancel', data: { callbackCallId } });
    }
    /**
     * The host ends a group call for everyone (p2p_end_for_all). Sent only on
     * a live, authenticated socket, never buffered: a request replayed after
     * a reconnect could end the call long after the host was told it had not.
     *
     * @returns {boolean} Whether the request went out.
     */
    endGroupCallForAll(callId, roomName) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'p2p_end_for_all', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'p2p_end_for_all', data: { callId, ...(roomName ? { roomName } : {}) } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'p2p_end_for_all' }, { console: false });
        return true;
    }
    /**
     * Stop ringing one invitee (conference_invite_cancel): the inviter, or
     * the host, while the ring is still ringing. Answered with
     * conference_invite_result { status: 'cancelled' } or an error with
     * operation 'conference_invite_cancel'. Sent only on a live socket, like
     * ending for everyone: a cancel replayed after a reconnect would be about
     * a ring that has long since changed.
     *
     * @returns {boolean} Whether the request went out.
     */
    /**
     * Start an instant meeting (a live call with only this client). Sent only
     * on a live authenticated socket, so a false return means nothing left.
     */
    startInstantMeeting(requestId) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'instant_meeting_start', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'instant_meeting_start', data: { requestId } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'instant_meeting_start' }, { console: false });
        return true;
    }
    cancelConferenceInvite(callId, targetId) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'conference_invite_cancel', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'conference_invite_cancel', data: { callId, targetId } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'conference_invite_cancel' }, { console: false });
        return true;
    }
    /**
     * The caller cancels a ring nobody has answered (p2p_cancel): the call
     * closes with zero duration and no CDR, and every ring stops
     * (outgoingRing.ts). It ends the whole call, so it is sent only on a live
     * socket, never replayed after a reconnect, when someone may have joined.
     * Answered with call_ended and p2p_call_cancelled, or an error without an
     * operation (FORBIDDEN, NOT_FOUND, CALL_ENDED…).
     *
     * @returns {boolean} Whether the request went out.
     */
    cancelP2PCall(callId, calleeId) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'p2p_cancel', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'p2p_cancel', data: { callId, calleeId: calleeId || null } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'p2p_cancel' }, { console: false });
        return true;
    }
    endActiveCall(departure) {
        // An observer page never ends, or forgets, the saved account's call.
        if (isObserverRoomPage()) {
            return;
        }
        const activeCall = getStoredJson('vrs_active_call');
        if (!activeCall?.callId) {
            return;
        }
        if (this.hasCallMovedAway(activeCall.callId)) {
            mobileLog('info', 'queue_call_end_suppressed_moved_away', {}, { console: false });
            removePersistentItem('vrs_active_call');
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_end',
            data: {
                callId: activeCall.callId,
                roomName: activeCall.roomName,
                // The page going away, not a hang-up: the server decides
                // (vrs-server/ws/call-reconnect-grace.js).
                ...departure ? { departure } : {}
            }
        });
        removePersistentItem('vrs_active_call');
    }
    /**
     * Tell the server this device's media connection for the call is up.
     * Completes a move of the client's call to this device: only now may the
     * other device leave without the interpreter's client pane going blank.
     */
    notifyCallRoomJoined(callId) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_room_joined',
            data: { callId }
        });
    }
    /**
     * This page is going away (reload, closed tab) without hanging up:
     * call_end with departure 'page_exit'. The server holds a one-to-one call,
     * interpreted (vrs-server/ws/call-reconnect-grace.js) or direct
     * (holdDirectCallOnPageExit in vrs-server/ws/p2p-handlers.js), for this
     * person to come back and treats any other call as the hang-up or leave
     * it always was. Sent only on a live, authenticated socket: the page is
     * unloading, so a buffered message would never leave.
     *
     * @param {string} callId - The call.
     * @param {string} [roomName] - Its room.
     * @returns {boolean} Whether it went out.
     */
    notifyPageExit(callId, roomName) {
        if (!callId || this.hasCallMovedAway(callId)
            || !this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return false;
        }
        this.ws.send(JSON.stringify({
            type: 'call_end',
            data: {
                callId,
                ...roomName ? { roomName } : {},
                departure: 'page_exit'
            }
        }));
        return true;
    }
    /**
     * This device's room for the call is up (its page loaded, or the app is
     * back in the conference), before its media: back from a reconnect grace
     * (vrs-server/ws/call-reconnect-grace.js). Media-up is call_room_joined.
     *
     * @param {string} callId - The call.
     * @returns {void}
     */
    notifyCallRoomEntered(callId) {
        if (!callId || this.hasCallMovedAway(callId)) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_room_entered',
            data: { callId }
        });
    }
    /**
     * This device could not join the call it was moving to (for example the
     * room is full). The server keeps the call on the other device.
     */
    notifyCallMoveFailed(callId, reason) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_move_failed',
            data: { callId, reason }
        });
    }
    /** Whether the server is holding this session out (another device is in a call). */
    isHeldElsewhere() {
        return this.heldElsewhere;
    }
    /**
     * While held out, ask again whether the other device's call has ended.
     * Answered by account_call_status. Sent on the open, unauthenticated
     * socket; never re-authenticates.
     */
    requestAccountCallStatus() {
        this.send({ type: 'account_call_status' });
    }
    /** The account signed in elsewhere and this socket was closed for it. */
    isSuperseded() {
        return this.superseded;
    }
    /**
     * "Use this device instead" after session_superseded: sign in here again,
     * which takes the queue back from the other device, and restore the
     * availability this device last asked for once the server confirms. Does
     * nothing unless this service was superseded.
     */
    retakeSupersededSession() {
        if (!this.superseded)
            return false;
        this.superseded = false;
        mobileLog('info', 'queue_ws_superseded_session_retaken', {}, { console: false });
        this.reconnect();
        return true;
    }
    /**
     * The user chose to use this device after the other device's call ended:
     * authenticate again. The server supersedes the other device if its call
     * is over, or holds this session out again if it is not.
     */
    retakeSession() {
        this.heldElsewhere = false;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.sendAuth();
            return;
        }
        this.reconnect();
    }
    send(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
        else {
            const now = Date.now();
            if (this.lastSendWarningLogAt === 0 || now - this.lastSendWarningLogAt >= SEND_WARNING_LOG_INTERVAL) {
                this.lastSendWarningLogAt = now;
                mobileLog('warn', 'queue_ws_send_skipped', {
                    messageType: message.type,
                    reason: 'not_connected',
                    url: this.config.queueServiceUrl
                }, { console: false });
            }
        }
    }
    /**
     * Sends a message that the server only accepts from an authenticated
     * socket. When the connection is still opening or awaiting auth_success
     * (e.g. cold app start, post-refresh re-auth), the message is buffered
     * and flushed as soon as authentication completes — instead of being
     * silently dropped while the UI shows a pending state.
     */
    sendWhenAuthenticated(message) {
        if (this.authenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            mobileLog('info', 'queue_ws_authed_message_sent', {
                messageType: message.type,
                wsReadyState: this.ws.readyState
            }, { console: false });
            return;
        }
        if (this.pendingAuthedMessages.length >= PENDING_AUTHED_MESSAGE_LIMIT) {
            this.pendingAuthedMessages.shift();
        }
        this.pendingAuthedMessages.push(message);
        mobileLog('info', 'queue_ws_message_buffered_for_auth', {
            messageType: message.type
        }, { console: false });
        // Held out by the server: re-sending auth would only be held out
        // again (and hammer it). The message waits for retakeSession().
        if (this.heldElsewhere) {
            return;
        }
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            mobileLog('info', 'queue_ws_reauth_for_buffered_message', {
                messageType: message.type,
                pendingCount: this.pendingAuthedMessages.length
            }, { console: false });
            this.sendAuth();
            return;
        }
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
            this.connect();
        }
    }
    flushPendingAuthedMessages() {
        while (this.pendingAuthedMessages.length > 0 && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const message = this.pendingAuthedMessages.shift();
            if (message) {
                this.ws.send(JSON.stringify(message));
                mobileLog('info', 'queue_ws_buffered_message_flushed', {
                    messageType: message.type,
                    remaining: this.pendingAuthedMessages.length
                }, { console: false });
            }
        }
    }
    isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
    getUserId() {
        return this.userId;
    }
    canAuthenticate() {
        return hasQueueAuthForRole(getUserRole());
    }
    closeSocket() {
        const socket = this.ws;
        this.ws = null;
        if (!socket)
            return;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        try {
            socket.close();
        }
        catch {
            mobileLog('warn', 'queue_ws_close_failed', {}, { console: false });
        }
    }
    disconnect() {
        this.shouldReconnect = false;
        this.authenticated = false;
        this.pendingAuthedMessages = [];
        for (const key of this.conferenceResponseTimers.keys())
            this.clearConferenceResponseTimer(key);
        this.conferenceResponses.clear();
        this.stopHeartbeat();
        this.clearStabilityTimer();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.closeSocket();
        this.presenceEventCleanup?.();
        this.presenceEventCleanup = null;
        this.listeners.clear();
        this.listenerGeneration++;
    }
    /**
     * Changes each time disconnect() removes every listener (sign-out). A
     * module that registers its listeners once compares it with the value of
     * its own registration, and registers again when it changed.
     */
    getListenerGeneration() {
        return this.listenerGeneration;
    }
    /**
     * The app came back to the foreground, or the network came back. A
     * signed-in account whose socket is down connects now, with a new set
     * of retries: the automatic retries stop after 10. An open socket gets a
     * heartbeat, so the watchdog finds a dead one. A socket closed on purpose
     * stays closed: sign-out, another device took the session, or the server
     * holds this one out.
     */
    resumeConnection(reason) {
        if (!this.shouldReconnect || this.superseded || this.heldElsewhere || !hasQueueAuthForRole(getUserRole())) {
            return false;
        }
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ping();
            return false;
        }
        if (this.ws?.readyState === WebSocket.CONNECTING) {
            return false;
        }
        mobileLog('info', 'queue_ws_resumed', { reason, reconnectAttempts: this.reconnectAttempts }, { console: false });
        this.reconnectAttempts = 0;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.connect();
        return true;
    }
    reconnect() {
        this.shouldReconnect = true;
        this.stopHeartbeat();
        // An explicit reconnect is fresh user intent (foregrounding, toggling
        // availability): forgive the exhausted automatic-retry counter so the
        // attempt ladder actually runs again.
        this.reconnectAttempts = 0;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.closeSocket();
        this.clearStabilityTimer();
        this.authenticated = false;
        this.connect();
    }
    ping() {
        this.send({ type: 'heartbeat', data: getPresenceHeartbeatPayload() });
    }
}
const hasWebSocketRuntime = typeof WebSocket !== 'undefined';
let _queueService = null;
function getQueueServiceInstance() {
    if (!hasWebSocketRuntime) {
        return null;
    }
    if (!_queueService) {
        _queueService = new InterpreterQueueService();
    }
    return _queueService;
}
export const queueService = {
    get instance() {
        return getQueueServiceInstance();
    },
    on: (event, callback) => queueService.instance.on(event, callback),
    off: (event, callback) => queueService.instance.off(event, callback),
    isConnected: () => Boolean(getQueueServiceInstance()?.isConnected()),
    canAuthenticate: () => Boolean(getQueueServiceInstance()?.canAuthenticate()),
    getUserId: () => getQueueServiceInstance()?.getUserId() || null,
    disconnect: () => getQueueServiceInstance()?.disconnect(),
    reconnect: () => getQueueServiceInstance()?.reconnect(),
    resumeConnection: (reason) => Boolean(getQueueServiceInstance()?.resumeConnection(reason)),
    listenerGeneration: () => getQueueServiceInstance()?.getListenerGeneration() ?? 0,
    ping: () => getQueueServiceInstance()?.ping(),
    send: (...args) => getQueueServiceInstance()?.send(...args),
    updateInterpreterStatus: (...args) => getQueueServiceInstance()?.updateInterpreterStatus(...args),
    getLastInterpreterStatus: () => getQueueServiceInstance()?.getLastInterpreterStatus() || null,
    holdAvailabilityReplay: () => getQueueServiceInstance()?.holdAvailabilityReplay(),
    requestInterpreter: (...args) => getQueueServiceInstance()?.requestInterpreter(...args),
    scheduleInterpreterRequest: (...args) => getQueueServiceInstance()?.scheduleInterpreterRequest(...args),
    endActiveCall: (departure) => getQueueServiceInstance()?.endActiveCall(departure),
    endCall: (callId, roomName, departure) => getQueueServiceInstance()?.endCall(callId, roomName, departure),
    sendExternalMeetingStatus: (...args) => getQueueServiceInstance()?.sendExternalMeetingStatus(...args),
    respondToGroupJoin: (callId, requestId, accept) => getQueueServiceInstance()?.respondToGroupJoin(callId, requestId, accept),
    endOpenCall: (callId, roomName) => getQueueServiceInstance()?.endOpenCall(callId, roomName),
    endGroupCallForAll: (callId, roomName) => getQueueServiceInstance()?.endGroupCallForAll(callId, roomName) === true,
    setPhoneCaptions: (callId, on) => getQueueServiceInstance()?.setPhoneCaptions(callId, on) === true,
    requestInterpreterCallback: (callId) => getQueueServiceInstance()?.requestInterpreterCallback(callId),
    cancelInterpreterCallback: (callbackCallId) => getQueueServiceInstance()?.cancelInterpreterCallback(callbackCallId),
    cancelConferenceInvite: (callId, targetId) => getQueueServiceInstance()?.cancelConferenceInvite(callId, targetId) === true,
    startInstantMeeting: (requestId) => getQueueServiceInstance()?.startInstantMeeting(requestId) === true,
    cancelP2PCall: (callId, calleeId) => getQueueServiceInstance()?.cancelP2PCall(callId, calleeId) === true,
    sendP2PCall: (...args) => getQueueServiceInstance()?.sendP2PCall(...args),
    sendGroupCall: (...args) => getQueueServiceInstance()?.sendGroupCall(...args),
    sendP2PRequestInterpreter: (...args) => getQueueServiceInstance()?.sendP2PRequestInterpreter(...args),
    announceLocalRoomJoin: (...args) => getQueueServiceInstance()?.announceLocalRoomJoin(...args),
    sendInterpreterCallClient: (...args) => getQueueServiceInstance()?.sendInterpreterCallClient(...args),
    acceptP2PCall: (...args) => getQueueServiceInstance()?.acceptP2PCall(...args),
    declineP2PCall: (...args) => getQueueServiceInstance()?.declineP2PCall(...args),
    respondToCallWaiting: (...args) => getQueueServiceInstance()?.respondToCallWaiting(...args),
    setCallHold: (callId, onHold) => getQueueServiceInstance()?.setCallHold(callId, onHold),
    checkHeldCall: (callId) => getQueueServiceInstance()?.checkHeldCall(callId),
    mergeHeldCall: (heldCallId, intoCallId) => getQueueServiceInstance()?.mergeHeldCall(heldCallId, intoCallId),
    prepareVriInvite: (...args) => getQueueServiceInstance()?.prepareVriInvite(...args),
    registerHandoffSession: (...args) => getQueueServiceInstance()?.registerHandoffSession(...args),
    unregisterHandoffSession: (...args) => getQueueServiceInstance()?.unregisterHandoffSession(...args),
    cancelRequest: (...args) => getQueueServiceInstance()?.cancelRequest(...args),
    acceptRequest: (...args) => getQueueServiceInstance()?.acceptRequest(...args),
    declineRequest: (...args) => getQueueServiceInstance()?.declineRequest(...args),
    acceptCallTransfer: (...args) => getQueueServiceInstance()?.acceptCallTransfer(...args),
    declineCallTransfer: (...args) => getQueueServiceInstance()?.declineCallTransfer(...args),
    transferCall: (...args) => getQueueServiceInstance()?.transferCall(...args),
    withdrawCallTransfer: (...args) => getQueueServiceInstance()?.withdrawCallTransfer(...args),
    requestTeam: (...args) => getQueueServiceInstance()?.requestTeam(...args),
    acceptTeam: (...args) => getQueueServiceInstance()?.acceptTeam(...args),
    declineTeam: (...args) => getQueueServiceInstance()?.declineTeam(...args),
    cancelTeamRequest: (...args) => getQueueServiceInstance()?.cancelTeamRequest(...args),
    leaveTeam: (...args) => getQueueServiceInstance()?.leaveTeam(...args),
    prepareRoomInvite: (...args) => getQueueServiceInstance()?.prepareRoomInvite(...args),
    ringClientAgain: (...args) => getQueueServiceInstance()?.ringClientAgain(...args),
    conferenceAdd: (...args) => getQueueServiceInstance()?.conferenceAdd(...args),
    notifyCallRoomJoined: (callId) => getQueueServiceInstance()?.notifyCallRoomJoined(callId),
    notifyPageExit: (callId, roomName) => Boolean(getQueueServiceInstance()?.notifyPageExit(callId, roomName)),
    notifyCallRoomEntered: (callId) => getQueueServiceInstance()?.notifyCallRoomEntered(callId),
    notifyCallMoveFailed: (callId, reason) => getQueueServiceInstance()?.notifyCallMoveFailed(callId, reason),
    requestAccountCallStatus: () => getQueueServiceInstance()?.requestAccountCallStatus(),
    retakeSession: () => getQueueServiceInstance()?.retakeSession(),
    isSuperseded: () => Boolean(getQueueServiceInstance()?.isSuperseded()),
    retakeSupersededSession: () => Boolean(getQueueServiceInstance()?.retakeSupersededSession()),
    // Malka Terp on or off the screen (vrs-server/ws/interpreter-standby.js).
    configureStandbyRing: (supported, appState) => getQueueServiceInstance()?.configureStandbyRing(supported, appState),
    setAppState: (appState) => getQueueServiceInstance()?.setAppState(appState),
    standbyRingActive: () => Boolean(getQueueServiceInstance()?.standbyRingActive()),
    leaveQueueForSignOut: () => getQueueServiceInstance()?.leaveQueueForSignOut(),
    isHeldElsewhere: () => Boolean(getQueueServiceInstance()?.isHeldElsewhere()),
    forgetCallMovedAway: (callId) => getQueueServiceInstance()?.forgetCallMovedAway(callId),
    hasCallMovedAway: (callId) => Boolean(getQueueServiceInstance()?.hasCallMovedAway(callId))
};
export default InterpreterQueueService;
